import type {NodeAttributes} from "../../../stem-core/ui/NodeAttributes";
import {UI} from "../../../stem-core/ui/UIBase";

import {styleRule, StyleSheet} from "../../../stem-core/ui/Style";
import {registerStyle} from "../../../stem-core/ui/style/Theme";
import {isSmallScreen} from "../../Utils";
import {Device} from "../../../stem-core/base/Device";
import {WidgetScrim} from "./WidgetScrim";
import {WidgetActiveRouteElement} from "./WidgetActiveRouteElement";
import {IndexExpanded} from "../pages/index-expanded/IndexExpanded";


export class WalletPopupStyle extends StyleSheet {
    // Built once and reused across redraws
    declare scrim?: WidgetScrim;
    @styleRule
    popup = {
        borderRadius: () => isSmallScreen() && !Device.isLandscape() ? 0 : 12,
        background: this.themeProps.WALLET_GRADIENT_BACKGROUND,
        width: "100%",
        transition: this.themeProps.DEFAULT_TRANSITION,
        position: "absolute",
        bottom: 0,
        transformOrigin: "bottom",
        zIndex: 3,
        opacity: 1,
        minWidth: 360,
        transform: "translateY(100%)",
        minHeight: "fit-content",
        display: "flex",
        flexDirection: "column",
    };

    @styleRule
    visible = {
        height: "100%",
        transform: "translateY(0)",
    };

    @styleRule
    hidden = {
        opacity: 0,
    };

    @styleRule
    card = {
        minHeight: "fit-content",
    }
}

export function WalletPopup(CardClass: typeof UI.Element<any>) {
    @registerStyle(WalletPopupStyle)
    class Popup extends WidgetActiveRouteElement {
        static instancesMap = new Map();
        visible = false;

        // Created and torn down alongside the popup
        declare scrim?: WidgetScrim;
        declare card?: any;
        declare ["constructor"]: typeof Popup;

        static getParentNode() {
            return IndexExpanded.instance.node;
        }

        extraNodeAttributes(attr: NodeAttributes) {
            super.extraNodeAttributes(attr);
            attr.addClass(this.styleSheet.popup);
            if (this.visible) {
                attr.addClass(this.styleSheet.visible);
            }
        }

        static show(options?: Record<string, any>) {
            if (this.instancesMap.get(this.name)) {
                return;
            }
            this.instancesMap.set(this.name, this.create(this.getParentNode(), options));
        }

        static hide() {
            const instance = this.instancesMap.get(this.name);
            if (!instance) {
                return;
            }
            instance.hide();
        }

        hide(duration = this.themeProps.DEFAULT_TRANSITION_DURATION_MS) {
            this.scrim.hide(duration);
            this.removeClass(this.styleSheet.visible);
            this.attachTimeout(() => this.destroyNode(), duration);
            this.scrim = null;
            this.constructor.instancesMap.set(this.constructor.name, null);
            this.visible = false;
        }

        render() {
            if (!this.scrim) {
                this.scrim = WidgetScrim.create(this.constructor.getParentNode());
            }
            return <CardClass ref="card" className={this.styleSheet.card}/>;
        }

        computeHeight() {
            return this.card.computeHeight();
        }

        onMount() {
            super.onMount();
            this.attachTimeout(() => {
                this.visible = true;
                this.redraw();
            });
        }
    }

    return Popup;
}
